/**
 * TschaukoVoiceOverlay — Realtime-Voice-Chatbot UI for Dr. Peter Tschauko.
 *
 * Reads session state + imperative methods from <AgentSessionContext>
 * (which is driven by a VoiceAgentController singleton mounted in
 * <TschaukoAgentRoot> at App-level). This component never creates a
 * session itself — it's just the visual layer.
 *
 * Visibility: shown when the session is in any active status and the
 * user hasn't minimized. Dismiss-actions:
 *   - X-Button → controller.close() (full terminate) + minimize
 *   - Drag-down → minimize (session stays alive, indicator in FooterNav)
 *   - Backdrop click → minimize
 *   - ESC → minimize
 */

import { useEffect, useState } from 'react'
import { useDragDismiss } from '../hooks/useDragDismiss'
import { useAgentSession } from '../lib/voice-agent/AgentSessionContext'
import type { SessionStatus } from '../lib/voice-agent/VoiceAgentController'
import { TschaukoGuideBody } from './TschaukoGuideBody'
import { TschaukoRealtimeSurface } from './TschaukoRealtimeSurface'
import '../tschauko-overlay.css'

interface Props {
  dark: boolean
}

const PRESENTABLE_STATUSES: ReadonlyArray<SessionStatus> = [
  'connecting',
  'ready',
  'speaking',
  'listening',
  'error',
]

export default function TschaukoVoiceOverlay({ dark }: Props) {
  const { snapshot, controller, isMinimized, minimize } = useAgentSession()
  // Drag-to-minimize via the shared building block (Issue #299 Etappe 4 —
  // this was a verbatim copy of EntdeckenOverlay's logic, now hooks/useDragDismiss).
  const { sheetRef, handleProps } = useDragDismiss(minimize)
  // Panel view: big live-video avatar (default) or the classic transcript form.
  // The transcript form (TschaukoGuideBody) stays available via this toggle.
  const [view, setView] = useState<'video' | 'chat'>('video')

  const visible = !isMinimized && PRESENTABLE_STATUSES.includes(snapshot.status)

  // Esc-to-minimize.
  useEffect(() => {
    if (!visible) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') minimize() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [visible, minimize])

  if (!visible) return null

  return (
    <div
      className={`tsch-overlay-root fv-page--${dark ? 'night' : 'light'}`}
      role="dialog"
      aria-modal="true"
      aria-label="Frag Dr. Tschauko"
    >
      <div className="tsch-overlay__backdrop" onClick={minimize} />

      <div
        ref={sheetRef}
        className={`tsch-overlay${(snapshot.focusedKnowledgeId != null || snapshot.focusedLocation != null) ? ' tsch-overlay--immersive' : ''}${view === 'video' ? ' tsch-overlay--video' : ''}`}
      >
        <div
          className="tsch-overlay__drag"
          {...handleProps}
        >
          <div className="tsch-overlay__grip" aria-hidden="true" />
          {/* Title block intentionally omitted — the portrait + status
              row below already identifies Dr. Tschauko. Having a big
              "Dr. Tschauko / Naturforscher" stacked above a Portrait
              circle next to "Dr. Peter Tschauko" duplicated the name
              twice on screen (Alex feedback 2026-06-30). The drag grip
              above stays as the visual handle. */}
        </div>

        {snapshot.focusedKnowledgeId == null && (
          <button
            type="button"
            className="tsch-overlay__toggle"
            onClick={() => setView((v) => (v === 'video' ? 'chat' : 'video'))}
            aria-label={view === 'video' ? 'Verlauf anzeigen' : 'Video anzeigen'}
          >
            {view === 'video' ? 'Verlauf' : 'Video'}
          </button>
        )}

        <button
          className="tsch-overlay__close"
          onClick={() => {
            // In immersive (knowledge focused) mode the X clears the
            // visualisation and returns to the transcript view — closing
            // the whole session by accident on a long-running media
            // exploration would be too easy. Outside immersive the X
            // does its normal "end session" job.
            if (snapshot.focusedKnowledgeId != null) {
              controller.clearFocusedKnowledge()
            } else {
              controller.close()
              minimize()
            }
          }}
          aria-label={snapshot.focusedKnowledgeId != null ? 'Darstellung schließen' : 'Sitzung beenden'}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>

        {view === 'video'
          ? <TschaukoRealtimeSurface presentation="dashboard" avatarVariantMode="midshot" />
          : <TschaukoGuideBody />}
      </div>
    </div>
  )
}
